import type { ReactElement } from "react";
import type { KundeAkte, Unternehmen } from "@kundeninfo/api-client";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { listText } from "../format.js";

/** Die für den Benutzer sichtbaren Unternehmen, zu denen der Kunde eine Beziehung hat. */
function sichtbareFirmen(kunde: KundeAkte, companies: readonly Unternehmen[]): Unternehmen[] {
  return kunde.relations
    .map((r) => companies.find((c) => c.id === r.companyId))
    .filter((c): c is Unternehmen => c !== undefined);
}

/**
 * Ob statt der Akte der Hinweis steht: Der Kunde hat keine Beziehung zum aktiven Unternehmen, aber
 * eine zu einem anderen sichtbaren — nur dann gibt es einen Weg dorthin. Ohne jede sichtbare
 * Beziehung (keine oder nur zu Unternehmen, die der Benutzer nicht sieht) zeichnet App die Akte
 * normal: Pfade über eine Kennung bleiben offen (E357), und ein Hinweis ohne Knopf wäre eine
 * Sackgasse.
 */
export function istFremdeAkte(
  kunde: KundeAkte, companies: readonly Unternehmen[], aktivId: string,
): boolean {
  return !kunde.relations.some((r) => r.companyId === aktivId)
    && sichtbareFirmen(kunde, companies).length > 0;
}

/**
 * Der Hinweis statt einer FREMDEN Akte (Spec Mandantentrennung, R18, Entscheid 360): Der Kunde hat
 * keine Beziehung zum aktiven Unternehmen — man kam über einen Link, ein Ticket oder den Konzern.
 * Je Unternehmen des Kunden ein Knopf „Zu <Kürzel> wechseln"; der Wechsel lässt die Adresse stehen,
 * nach dem Neuladen steht die Akte da. Keine Daten der Akte werden gezeigt — nur, wohin sie gehört.
 */
export function FremdeAkte(p: {
  kunde: KundeAkte; companies: readonly Unternehmen[]; onWechseln: (companyId: string) => void;
}): ReactElement {
  const firmen = sichtbareFirmen(p.kunde, p.companies);
  const wohin = firmen.length === 1 ? firmen[0]!.name : listText(firmen.map((c) => c.shortCode));
  return (
    <Hinweis art="info" className="app__meldung" aktionen={<>{firmen.map((c) => (
      <KiKnopf key={c.id} onClick={() => p.onWechseln(c.id)}>Zu {c.shortCode} wechseln</KiKnopf>
    ))}</>}>
      {p.kunde.companyName} gehört zu {wohin}.
    </Hinweis>
  );
}
